// noinspection JSIgnoredPromiseFromCall

import { noop } from '@accedo/general-utils';
import { logger } from '@accedo/logger';
import { Location } from 'preact-router';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';
import { ID3TagData, PlayerStates } from '##/components/Player/PlayerTypes';
import { Messengers } from '##/components/Player/utils/Messengers';
import { StandardCarouselItem } from '##/components/StandardCarousel/types';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { ShowApiResponse } from '##/services/tenApi/common/types';
import {
  ClientSideAd,
  VodDAIAdBreak,
  VODPlaybackResponse,
} from '##/services/tenApi/dai/types';
import { PageNavigation } from '##/utils/PageNavigation';
import { runOnce } from '##/utils';
import { ListenerData } from '##/utils/types';
import { LiveChannel } from '##/services/tenApi/collections/types';
import { OzTamClient } from './OzTamClient';
import { getOztamLiveMediaId } from './utils';

const PROGRESS_TIMER = 60 * 1000;

const isoDateStringRegex = /(\d{4}-\d{2}-\d{2})[A-Z]+(\d{2}:\d{2}:\d{2}).([0-9+-:]+[a-z]{0,1})/g;

export const OZTAM_VOD_PUBLISHER_ID = '2199827728001';

export const NOOP_BOOLEAN = (): boolean => true;
const NOOP_PROMISE = (): Promise<void> => Promise.resolve();

class OzTamBridge {
  private client = new OzTamClient();
  private lastRoute = '';

  private currentAd: ClientSideAd | null = null;
  private contentHasPlayed = false;
  private vodAdBreaks: VodDAIAdBreak[] = [];

  private finishedPrerollIfExists = true;
  private waitingForNextPlayEventBeforeSendingProgress = false;
  private seekToOnAdFinishTime = -1;

  private startingInLiveAdBreak = false;
  private startedInLiveAdBreak = false;

  private currentTime = 0;
  private currentTimeNoAds = 0;
  private duration = 0;
  private hasSentBeginEvent = false;
  private hasSentCompleteEvent = false;
  private progressInterval = 0;

  private playerState: PlayerStates = PlayerStates.Stopped;
  private isLive = false;
  private removeRouteChangeListener = noop;
  private sendLoadEvent = NOOP_PROMISE;
  private hasSentLoadEvent = NOOP_BOOLEAN;
  private startBufferTime = 0;
  private receivers: ListenerData[] = [];
  private hasReceivedLiveProgramDateTime = false;
  private sessionManuallyRegenerated = false;

  init = async (isLiveSession = false): Promise<void> => {
    [this.sendLoadEvent, this.hasSentLoadEvent] = runOnce(
      this.client.loadEvent,
    );
    logger.info('[OzTamBridge] init() called here');
    await this.client.init();
    await this.client.sendVozStreamingRequest(isLiveSession);

    this.removeRouteChangeListener = PageNavigation.addRouteChangeListener(
      this.onRouteChange,
    );

    this.receivers = [
      ...this.receivers,
      Messengers.playerState$.addReceiver(this.onPlayerStateChange),
      Messengers.currentAd$.addReceiver(this.onAdStateChange),
      Messengers.vodInfo$.addReceiver(this.onVodDAIInfoChange),
      Messengers.currentTime$.addReceiver(this.onTimeUpdate),
      Messengers.isLive$.addReceiver(this.onIsLiveChange),
      Messengers.userRequestsState$.addReceiver(this.onUserRequestsState),
      Messengers.duration$.addReceiver(this.onDurationChange),

      Messengers.seekToAfterAd$.addReceiver(this.onSeekedPastAd),
      Messengers.fragTagParsed$.addReceiver(this.onFragTagParsed),
      Messengers.liveProgramTimeAndTargetBuffer$.addReceiver(
        this.liveProgramDateTimeReceiver,
      ),
      Messengers.recreateOztamSession$.addReceiver(this.recreateOzTamSession),
      Messengers.contentResumeRequested$.addReceiver(this.onContentResume),
      Messengers.setLastOztamPlaybackPosition$.addReceiver(
        this.setLastPlaybackPosition,
      ),
    ];

    await this.onRouteChange({ location: window.location });
    this.waitingForNextPlayEventBeforeSendingProgress = false;
  };

  private setLastPlaybackPosition = (time: number): void => {
    logger.debug(
      '[OztamBridge] setting last playback position throughout to: ',
      time,
    );

    this.currentTime = time;
    this.currentTimeNoAds = time;
    this.client.setLastPosition(time);
  };

  private recreateOzTamSession = (): void => {
    this.client.refreshOzTamSession(true);
  };

  private startProgressInterval = (): void => {
    this.clearProgressInterval();
    this.progressInterval = window.setInterval(() => {
      logger.debug('oztam progress interval tick', this.getCurrentTime());
      this.sendProgressEvent(this.getCurrentTime());
    }, PROGRESS_TIMER);
  };

  private clearProgressInterval = (): void => {
    clearInterval(this.progressInterval);
  };

  private liveProgramDateTimeReceiver = ({
    programTime,
    startBufferTime,
  }: {
    programTime: number;
    startBufferTime: number;
  }): void => {
    if (this.hasSentLoadEvent() || !this.isLive) {
      return;
    }

    this.hasReceivedLiveProgramDateTime = true;

    this.startBufferTime = startBufferTime;

    this.client.setPtsEpochDiff(programTime, true);

    this.sendLoadEvent();

    this.client.setPtsEpochDiff(programTime - this.startBufferTime, true);
  };

  private onRouteChange = async ({
    location: { pathname },
  }: {
    location: Location;
  }): Promise<void> => {
    const hasRouteChanged = this.lastRoute !== pathname;

    if (this.lastRoute.includes('player/live') && pathname !== '/loading') {
      logger.info('[onRouteChange] exit live player route');

      this.client.onLivePlayerRouteExit();
    }

    this.lastRoute = pathname;

    if (pathname.includes('/player')) {
      if (hasRouteChanged) {
        await this.onNavToPlayer();
      }
    } else {
      this.reset();
    }
  };

  private reset = (): void => {
    logger.info('[OzTamBridge] reset() called here');
    Messengers.removeReceivers(this.receivers);
    if (this.playerState === PlayerStates.Playing) {
      this.sendProgressEvent(this.getCurrentTime());
    }
    this.contentHasPlayed = false;
    this.finishedPrerollIfExists = true;
    this.waitingForNextPlayEventBeforeSendingProgress = false;
    this.currentAd = null;
    this.isLive = false;
    this.vodAdBreaks = [];
    this.currentTime = 0;
    this.currentTimeNoAds = 0;
    this.startBufferTime = 0;
    this.duration = 0;
    this.startingInLiveAdBreak = false;
    this.startedInLiveAdBreak = false;
    this.clearProgressInterval();
    this.client.clearTimers();
    this.client.reset();
    this.lastRoute = '';
    Messengers.ozTamSessionId$.send('');
    this.removeRouteChangeListener();
    this.hasSentBeginEvent = false;
    this.hasSentCompleteEvent = false;
    this.hasReceivedLiveProgramDateTime = false;
    this.receivers = [];
    this.sessionManuallyRegenerated = false;
  };

  private onNavToPlayer = async (): Promise<void> => {
    const episodeState = PageNavigation.getCurrentState() as
      | StandardCarouselItem
      | LiveCarouselItem
      | null;

    const isVod = !!(episodeState as StandardCarouselItem)?.showApiEndpoint;
    const isLive = !!(episodeState as LiveCarouselItem)?.liveShow;

    try {
      switch (true) {
        case isVod: {
          const vodEpisode = episodeState as StandardCarouselItem;
          const showEndpoint = vodEpisode.showApiEndpoint;

          const { episodeCrid, referenceId, altId } = vodEpisode || {};

          const response = await sendApiRequest<ShowApiResponse[]>(
            showEndpoint,
          );
          const showResponse = response[0];

          this.client.initVideo(
            {
              publisherId: OZTAM_VOD_PUBLISHER_ID,
              mediaDuration: vodEpisode.duration,
              mediaId: episodeCrid || altId || referenceId,
              channel: vodEpisode.tvChannel || 'none-found',
              mediaType: 'vod',
              programName: showResponse.name,
              seriesId: vodEpisode.seriesCrid,
              episodeName: vodEpisode.title,
              seriesName: vodEpisode.tvShow,
              genre: showResponse.genre?.label || 'none-found',
              classification: showResponse.classification,
            },
            false,
          );
          break;
        }
        case isLive: {
          const liveVideo = episodeState as LiveCarouselItem | LiveChannel;

          const { publisherId, key = '', liveShow } =
            (liveVideo as LiveChannel) || {};

          this.client.initVideo(
            {
              publisherId: publisherId || '',
              mediaDuration: 0,
              mediaId: getOztamLiveMediaId(liveVideo),
              channel: key,
              mediaType: 'live',
              programName: liveShow?.title || '',
              seriesId: liveShow?.id || '',
              seriesName: liveShow?.title || '',
              genre: liveShow?.genre || '',
            },
            true,
          );
          break;
        }
        default:
          logger.warn('ozTam client: no player info passed');
          break;
      }

      if (isVod || !isLive) {
        this.sendLoadEvent();
      }
    } catch (err) {
      logger.error(err);
    }
  };

  private sendCompleteEvent = (time: number): void => {
    if (this.hasSentCompleteEvent && !this.isLive) {
      return;
    }

    this.hasSentCompleteEvent = true;

    this.client.completeEvent(time);
  };

  private sendBeginEvent = (time: number): void => {
    if (this.isLive && !this.hasSentLoadEvent()) {
      logger.debug(
        '[OztamBridge] synchronise BEGIN with LOAD when not starting in ad break',
      );

      this.sendLoadEvent()
        .then(() => this.clientBeginEvent(time))
        .catch(() => this.clientBeginEvent(time));

      return;
    }

    this.clientBeginEvent(time);
  };

  private clientBeginEvent = (time: number): void => {
    if (!this.duration && !this.isLive) {
      return;
    }

    this.hasSentBeginEvent = true;

    this.client.beginEvent(time);
  };

  private getCurrentTime = (): number => {
    if (!this.isLive) {
      return this.currentTimeNoAds;
    }

    return this.currentTimeNoAds || this.startBufferTime;
  };

  private onPlayerStateChange = async (
    playerState: PlayerStates,
  ): Promise<void> => {
    const previousState = this.playerState;
    this.playerState = playerState;
    if (this.isLive) {
      if (playerState === PlayerStates.Ended) {
        this.sendCompleteEvent(this.getCurrentTime());
      } else if (playerState === PlayerStates.Playing) {
        if (
          previousState !== PlayerStates.Playing &&
          previousState !== PlayerStates.Seeking
        ) {
          if (!this.contentHasPlayed) {
            this.contentHasPlayed = true;
            if (!this.startingInLiveAdBreak) {
              this.sendBeginEvent(this.getCurrentTime());
              this.startProgressInterval();
            }
          } else if (!this.currentAd) {
            // Resume playing content after pause, seek etc
            logger.debug('oztam resumed');
            const sessionRecreated = this.client.wasSessionJustRegenerated();

            const currentTimeValueForNewSession = sessionRecreated
              ? Math.max(this.getCurrentTime(), this.client.getLastPosition())
              : this.getCurrentTime();

            this.client.playingEvent(currentTimeValueForNewSession);
            this.startProgressInterval();
            this.client.setLastPosition(currentTimeValueForNewSession);

            this.sessionManuallyRegenerated = sessionRecreated;
          }
        }
      }

      // Log progress from last interval to when stream was paused
      // or seeked, buffering
      if (
        playerState !== PlayerStates.Playing &&
        previousState === PlayerStates.Playing &&
        !this.currentAd
      ) {
        logger.debug('oztam no longer playing');
        const currentTimeValueForNewSession = this.sessionManuallyRegenerated
          ? Math.max(this.getCurrentTime(), this.client.getLastPosition())
          : this.getCurrentTime();

        this.sendProgressEvent(currentTimeValueForNewSession);
        this.clearProgressInterval();
      }
    } else {
      if (playerState === PlayerStates.Ended) {
        if (!this.currentAd) {
          this.client.completeEvent(this.currentTimeNoAds);
        }
      } else if (playerState === PlayerStates.Playing) {
        if (
          previousState !== PlayerStates.Playing &&
          previousState !== PlayerStates.Seeking &&
          this.contentHasPlayed
        ) {
          // Resume playing content after pause, seek etc
          logger.debug('oztam resumed');
          if (!this.currentAd) {
            this.startProgressInterval();
            this.client.setLastPosition(this.getCurrentTime());
          }
          this.client.playingEvent(this.getCurrentTime());
          this.waitingForNextPlayEventBeforeSendingProgress = false;
        }

        const csAdsManager = Messengers.csAdsManager$.getCurrentValue();

        if (
          !this.contentHasPlayed &&
          this.finishedPrerollIfExists &&
          !Messengers.resumeWatchingData$.getCurrentValue() &&
          csAdsManager &&
          ((csAdsManager as unknown) as { isContent: boolean })?.isContent
        ) {
          if (Messengers.currentAd$.getCurrentValue()) {
            return;
          }
          this.contentHasPlayed = true;
          this.sendBeginEvent(Math.floor(this.getCurrentTime()));
          this.startProgressInterval();
        }
      }

      // Log progress from last interval to when stream was paused
      // or seeked, buffering
      if (
        playerState !== PlayerStates.Playing &&
        previousState === PlayerStates.Playing &&
        this.contentHasPlayed &&
        !this.waitingForNextPlayEventBeforeSendingProgress
      ) {
        logger.debug('oztam no longer playing: ', this.getCurrentTime());

        if (playerState === PlayerStates.Paused) {
          this.sendProgressEvent(this.getCurrentTime());
          this.client.pauseEvent();
          this.clearProgressInterval();
        }
      }

      if (
        playerState === PlayerStates.Seeked &&
        !this.contentHasPlayed &&
        !this.currentAd &&
        !this.isLive &&
        this.currentTime > 0
      ) {
        logger.debug('oztam removing preroll restriction');
        // Resume popup showed so we can start directly with content
        this.finishedPrerollIfExists = true;
        this.contentHasPlayed = true;
        if (this.isLive) this.client.setLastPosition(this.getCurrentTime());
        this.sendBeginEvent(this.getCurrentTime());
      }
    }
  };

  private onContentResume = (): void => {
    // added to handle case of when ads failed to fetch
    const csAdsManager = Messengers.csAdsManager$.getCurrentValue();

    if (
      !this.contentHasPlayed &&
      !Messengers.resumeWatchingData$.getCurrentValue() &&
      ((csAdsManager as unknown) as { isContent: boolean })?.isContent &&
      !this.hasSentBeginEvent
    ) {
      logger.debug('[OzTamBridge] sending begin event if not already sent...');
      this.contentHasPlayed = true;
      this.sendBeginEvent(Math.floor(this.getCurrentTime()));
      this.startProgressInterval();
    }
  };

  private onDurationChange = (duration: number): void => {
    this.duration = duration;
  };

  private onUserRequestsState = (playerState: PlayerStates): void => {
    if (playerState === PlayerStates.Ended) {
      const durationNoAds = this.subtractAdTime(
        this.duration,
        this.vodAdBreaks,
      );
      logger.debug('oztam sending complete event with duration', durationNoAds);
      this.client.setLastPosition(durationNoAds);
      this.sendCompleteEvent(durationNoAds);
    }
  };

  private sendProgressEvent = (
    time: number,
    firstAd = false,
  ): Promise<void> => {
    if (this.isLive && this.client.wasSessionJustRegenerated()) {
      logger.debug('oztam prevent PROGRESS event sent between oztam sessions');

      return Promise.resolve();
    }

    this.sessionManuallyRegenerated = false;

    if (!this.hasSentBeginEvent) {
      logger.debug('oztam prevent PROGRESS event sent before BEGIN');

      return Promise.resolve();
    }

    if (this.hasSentCompleteEvent && !this.isLive) {
      return Promise.resolve();
    }

    return this.client.progressEvent(time, firstAd);
  };

  private sendLiveAdChangeEventSequence = async (): Promise<void> => {
    // prevent these events from arriving out of order

    const currentTime = this.getCurrentTime();

    await this.client.adCompleteEvent();
    await this.sendProgressEvent(currentTime);
    await this.client.adBeginEvent();
  };

  private sendLiveAdBreakEndSequence = async (): Promise<void> => {
    // prevent these events from arriving out of order
    if (this.isLive && this.client.wasSessionJustRegenerated()) {
      logger.debug(
        'oztam prevent live ad break end sequence if session re-generated',
      );

      return;
    }

    const currentTime = this.getCurrentTime();

    await this.client.adCompleteEvent();
    await this.sendProgressEvent(currentTime);
  };

  private sendVodAdChangeSequence = async (): Promise<void> => {
    // prevent these events from arriving out of order

    await this.client.adCompleteEvent();
    await this.client.adBeginEvent();
  };

  private onAdStateChange = async (
    currentAd: ClientSideAd | null,
  ): Promise<void> => {
    const previousAd = this.currentAd;
    const hasAdChangedWithinSameSlot =
      previousAd && currentAd && previousAd.ad_id !== currentAd.ad_id;
    if (this.isLive) {
      // AD CHANGE IN BREAK
      if (hasAdChangedWithinSameSlot) {
        this.sendLiveAdChangeEventSequence();

        // AD BREAK END
      } else if (previousAd && !currentAd) {
        this.sendLiveAdBreakEndSequence();
        this.startProgressInterval();

        // AD BREAK START
      } else if (!previousAd && currentAd) {
        if (this.startingInLiveAdBreak) {
          this.sendBeginEvent(this.getCurrentTime());
          this.startingInLiveAdBreak = false;
          this.contentHasPlayed = true;
        } else {
          this.sendProgressEvent(this.getCurrentTime());
        }
        this.clearProgressInterval();
        this.client.adBeginEvent();
      }
    } else {
      // AD CHANGE WITHIN BREAK
      // eslint-disable-next-line no-lonely-if
      if (hasAdChangedWithinSameSlot) {
        this.sendVodAdChangeSequence();

        // AD BREAK END
      } else if (previousAd && !currentAd) {
        this.client.adCompleteEvent();

        // ad break was preroll
        if (!this.finishedPrerollIfExists) {
          this.contentHasPlayed = true;
          this.finishedPrerollIfExists = true;
          this.sendBeginEvent(Math.floor(this.getCurrentTime()));
          this.startProgressInterval();
        } else if (this.seekToOnAdFinishTime > 0) {
          logger.debug('oztam finished ad and setting seek time');
          // expect a seek so hold off on sending progress events until
          // player enters playing state again
          this.waitingForNextPlayEventBeforeSendingProgress = true;

          this.client.setLastPosition(
            this.subtractAdTime(this.seekToOnAdFinishTime, this.vodAdBreaks),
          );
        } else {
          this.startProgressInterval();
        }

        // AD BREAK START
      } else if (!previousAd && currentAd) {
        if (this.finishedPrerollIfExists) {
          // content played into midroll so log that progress
          // before we clear the progress interval
          this.sendProgressEvent(this.currentTimeNoAds, true);
        }

        this.clearProgressInterval();
        this.client.adBeginEvent();
      }
    }
    this.currentAd = currentAd;
  };

  private onVodDAIInfoChange = (
    newAdInfo: VODPlaybackResponse | null,
  ): void => {
    this.vodAdBreaks = newAdInfo?.ad_breaks || [];
    if (!this.contentHasPlayed && this.vodAdBreaks[0]?.type === 'pre') {
      this.finishedPrerollIfExists = false;
    }
  };

  private onTimeUpdate = (time: number): void => {
    this.currentTime = time;
    this.currentTimeNoAds = time; // No need to subtract Ad breaks since ad playback and content playback are now seperate
  };

  private onIsLiveChange = (newIsLive: boolean): void => {
    this.isLive = newIsLive;
    if (this.isLive) {
      this.finishedPrerollIfExists = true;
    }
  };

  private onFragTagParsed = (tagData: ID3TagData | null): void => {
    if (tagData?.tag) {
      const isAdTag = tagData.tag.includes('google');
      if (isAdTag) {
        if (!this.contentHasPlayed) {
          this.startingInLiveAdBreak = true;
          this.startedInLiveAdBreak = true;

          if (!this.hasReceivedLiveProgramDateTime) {
            this.sendLoadEvent();
          }
        }
      } else if (!this.startedInLiveAdBreak) {
        // only set a pts epoch diff if we start in content
        // otherwise we won't know the correct starting value
        // and will jump back in time when content resumes
        const isoDateString = tagData.tag.match(isoDateStringRegex)?.[0] || '';
        const tagUnixTime =
          new Date(isoDateString.toUpperCase()).getTime() / 1000;
        if (tagUnixTime) {
          this.client.setPtsEpochDiff(tagUnixTime - tagData.pts);
          this.client.setStopRecordingEvents(false);
        } else {
          this.client.setStopRecordingEvents(true);
        }
      }
    }
  };

  private onSeekedPastAd = (time: number): void => {
    logger.debug('oztam set seek past ad time', time);
    this.seekToOnAdFinishTime = time;
  };

  private subtractAdTime = (time: number, adBreaks: VodDAIAdBreak[]): number =>
    time -
    adBreaks.reduce((totalTime, adBreak) => {
      if (adBreak.start < this.currentTime) {
        if (adBreak.start + adBreak.duration > this.currentTime) {
          return totalTime + (this.currentTime - adBreak.start);
        }
        return totalTime + adBreak.duration;
      }
      return totalTime;
    }, 0);
}

export default new OzTamBridge();
